import { useMutation } from "@apollo/client/react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Form, Input, Modal } from "antd";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { CreateProducerDocument } from "../../gql/generated/graphqlSchema.tsx";
import { routes } from "../../routes.ts";

type FormValues = {
  companyName: string;
};

export const CreateProducerModal = ({
  open,
  onClose,
  initialName = "",
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly initialName?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();

  const [createProducer, { loading }] = useMutation(CreateProducerDocument, {
    onCompleted: (data) => {
      onClose();
      void navigate(routes.producerDetail.to(data.createProducer.id));
    },
  });

  const handleOk = () => {
    form.validateFields().then((values) => {
      void createProducer({ variables: { input: { companyName: values.companyName } } });
    });
  };

  return (
    <Modal
      afterClose={() => form.resetFields()}
      afterOpenChange={(visible) => {
        if (visible) form.setFieldsValue({ companyName: initialName });
      }}
      cancelButtonProps={{ icon: <CloseOutlined /> }}
      confirmLoading={loading}
      destroyOnHidden
      okButtonProps={{ icon: <PlusOutlined /> }}
      okText={t("common.create")}
      onCancel={onClose}
      onOk={handleOk}
      open={open}
      title={t("producers.createProducer")}
    >
      <Form form={form} layout="vertical" onFinish={handleOk}>
        <Form.Item
          label={t("producers.companyName")}
          name="companyName"
          rules={[{ required: true, message: t("producers.validation.companyNameRequired") }]}
        >
          <Input autoFocus placeholder={t("producers.namePlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
